import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { changePassword } from 'aws-auth-lib';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AvailabilityScheduleCard from '../components/users/AvailabilityScheduleCard';
import CallPriorityField from '../components/users/CallPriorityField';
import { useUser } from '../hooks/useUser';
import { fetchUserByRole, updateUserByRole } from '../services/users';
import { normalizeCallPriority } from '../utils/callPriority';
import { passwordPolicyMessage, validatePasswordPolicy } from '../utils/passwordPolicy';

const clinicianTypeOptions = [
    { value: 'NURSING', label: 'Enfermagem' },
    { value: 'CARDIOLOGY', label: 'Cardiologia' },
    { value: 'GERIATRICS', label: 'Geriatria' },
    { value: 'NUTRITION', label: 'Nutrição' },
    { value: 'NEUROLOGY', label: 'Neurologia' },
    { value: 'PSYCHIATRY', label: 'Psiquiatria' },
    { value: 'PSYCHOLOGY', label: 'Psicologia' },
    { value: 'PHYSICAL_THERAPY', label: 'Fisioterapia' },
    { value: 'OCCUPATIONAL_THERAPY', label: 'Terapia Ocupacional' },
    { value: 'SOCIAL_ASSISTANT', label: 'Assistente Social' },
    { value: 'GENERAL_PRACTICE', label: 'Medicina Geral' }
];

const actionSecondarySx = {
    bgcolor: '#FFFFFF',
    color: '#3D3D3D',
    border: '2px solid #D9D9D9',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#F7F7F7',
        boxShadow: 'none'
    }
};

const actionPrimarySx = {
    bgcolor: '#D63731',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#C7312C',
        boxShadow: 'none'
    }
};

const readOnlyViewFieldSx = {
    '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: '#6F6F6F',
        color: '#6F6F6F'
    },
    '& .MuiInputBase-input.Mui-disabled::placeholder': {
        WebkitTextFillColor: '#8A8A8A'
    },
    '& .MuiSelect-select.Mui-disabled': {
        WebkitTextFillColor: '#6F6F6F',
        color: '#6F6F6F'
    }
};

const AVAILABILITY_ACTIVE_DAYS_KEY = 'availability_active_days';
const AVAILABILITY_NOTES_KEY = 'availability_notes';
const VALID_AVAILABILITY_DAYS = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

const normalizeAvailabilityDays = (value) => {
    const rawDays = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            : [];

    return rawDays
        .map((item) => String(item).trim().toLowerCase())
        .filter((item) => VALID_AVAILABILITY_DAYS.has(item));
};

const toApiDate = (date) => {
    if (!date) {
        return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (!match) {
        return undefined;
    }
    return `${match[3]}-${match[2]}-${match[1]}`;
};

const toPickerValue = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const MyProfile = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [readOnly, setReadOnly] = useState(true);
    const [form, setForm] = useState({
        roleUuid: '',
        name: '',
        email: '',
        birthDate: '',
        street: '',
        zip: '',
        city: '',
        province: '',
        country: '',
        phone: '',
        professionalCard: '',
        professionalCardExpiry: '',
        workplace: '',
        clinicianType: '',
        callPriority: 5,
        coordinator: false,
        notes: '',
        availabilityActiveDays: [],
        availabilityNotes: ''
    });
    const [snapshot, setSnapshot] = useState(null);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const onFieldChange = (field) => (event) => {
        const value =
            event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const onPasswordFieldChange = (field) => (event) => {
        const value = event?.target?.value || '';
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        setPasswordError('');
        setPasswordSuccess('');
    };

    useEffect(() => {
        const loadProfile = async () => {
            if (!user.selectedRole) {
                return;
            }

            setLoading(true);
            setError('');
            try {
                const response = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: user.selectedRole
                });
                const data = response?.data || {};
                const extra = data.user_data || {};

                const nextForm = {
                    roleUuid: data.role_uuid || user.selectedRole,
                    name: data.name || '',
                    email: data.email || '',
                    birthDate: data.birth_date || '',
                    street: data.address?.street || '',
                    zip: data.address?.zip || '',
                    city: data.address?.city || '',
                    province: data.address?.province || '',
                    country: data.address?.country || '',
                    phone: data.phone || '',
                    professionalCard: extra.professional_card || '',
                    professionalCardExpiry: extra.professional_card_expiry || '',
                    workplace: extra.workplace || '',
                    clinicianType: data.clinician_type || '',
                    callPriority: normalizeCallPriority(data.call_priority),
                    coordinator: data.role === 'ACCOUNT_ADMIN',
                    notes: extra.notes || '',
                    availabilityActiveDays: normalizeAvailabilityDays(
                        data[AVAILABILITY_ACTIVE_DAYS_KEY] ?? extra[AVAILABILITY_ACTIVE_DAYS_KEY]
                    ),
                    availabilityNotes:
                        data[AVAILABILITY_NOTES_KEY] ?? extra[AVAILABILITY_NOTES_KEY] ?? ''
                };

                setForm(nextForm);
                setSnapshot(nextForm);
                setReadOnly(true);
            } catch (err) {
                setError(err?.response?.data?.description || 'Nao foi possivel carregar o perfil.');
            } finally {
                setLoading(false);
            }
        };

        void loadProfile();
    }, [user.selectedRole]);

    const onEdit = () => {
        setReadOnly(false);
    };

    const onCancel = () => {
        if (snapshot) {
            setForm(snapshot);
        }
        setError('');
        setReadOnly(true);
    };

    const handleToggleAvailabilityDay = (dayKey) => {
        setForm((prev) => ({
            ...prev,
            availabilityActiveDays: prev.availabilityActiveDays.includes(dayKey)
                ? prev.availabilityActiveDays.filter((item) => item !== dayKey)
                : [...prev.availabilityActiveDays, dayKey]
        }));
    };

    const onSubmit = async () => {
        if (!user.selectedRole || !form.roleUuid) {
            setError('Sem role ativa para editar perfil.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            await updateUserByRole({
                userRoleUuid: user.selectedRole,
                payload: {
                    role_uuid: form.roleUuid,
                    name: form.name.trim() || undefined,
                    birth_date: toApiDate(form.birthDate),
                    phone: form.phone.trim() || undefined,
                    address: {
                        street: form.street.trim() || undefined,
                        zip: form.zip.trim() || undefined,
                        city: form.city.trim() || undefined,
                        province: form.province.trim() || undefined,
                        country: form.country.trim().toUpperCase() || undefined
                    },
                    user_data: {
                        professional_card: form.professionalCard.trim() || null,
                        professional_card_expiry: form.professionalCardExpiry.trim() || null,
                        workplace: form.workplace.trim() || null,
                        notes: form.notes.trim() || null,
                        [AVAILABILITY_ACTIVE_DAYS_KEY]: form.availabilityActiveDays,
                        [AVAILABILITY_NOTES_KEY]: form.availabilityNotes.trim() || null
                    },
                    clinician_type: form.clinicianType || undefined,
                    call_priority: normalizeCallPriority(form.callPriority)
                }
            });

            const nextSnapshot = { ...form };
            setSnapshot(nextSnapshot);
            setForm(nextSnapshot);
            setReadOnly(true);
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel atualizar o perfil.');
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmitPasswordChange = async () => {
        const { currentPassword, newPassword, confirmPassword } = passwordForm;

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError('Preencha todos os campos da password.');
            setPasswordSuccess('');
            return;
        }

        if (!validatePasswordPolicy(newPassword)) {
            setPasswordError(passwordPolicyMessage);
            setPasswordSuccess('');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('As passwords devem coincidir.');
            setPasswordSuccess('');
            return;
        }

        if (currentPassword === newPassword) {
            setPasswordError('A nova password deve ser diferente da password atual.');
            setPasswordSuccess('');
            return;
        }

        setPasswordSubmitting(true);
        setPasswordError('');
        setPasswordSuccess('');

        try {
            await changePassword(currentPassword, newPassword);
            setPasswordSuccess('Password alterada com sucesso.');
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        } catch (err) {
            setPasswordError(err?.message || 'Nao foi possivel alterar a password.');
        } finally {
            setPasswordSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#fff' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Paper
                    variant="outlined"
                sx={{
                    mt: 2,
                    p: { xs: 1.5, md: 2.5 },
                    bgcolor: '#fff',
                    borderRadius: 2
                }}
                >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <IconButton
                        sx={{
                            width: 40,
                            height: 40,
                            border: '1.5px solid #BDBDBD',
                            bgcolor: '#FFFFFF',
                            color: '#3D3D3D',
                            borderRadius: '12px',
                            '&:hover': {
                                bgcolor: '#F7F7F7'
                            }
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIosNewRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>
                        Ficha Pessoal
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2,
                        ...(readOnly ? readOnlyViewFieldSx : {})
                    }}
                >
                    <TextField
                        label="Nome"
                        placeholder="Nome completo"
                        fullWidth
                        value={form.name}
                        onChange={onFieldChange('name')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Email"
                        placeholder="utilizador@dominio.com"
                        fullWidth
                        value={form.email}
                        disabled
                    />
                    <DatePicker
                        label="Data de Nascimento"
                        format="DD/MM/YYYY"
                        disabled={readOnly || loading || submitting}
                        value={toPickerValue(form.birthDate)}
                        onChange={(newValue) =>
                            onFieldChange('birthDate')({
                                target: {
                                    value:
                                        newValue && dayjs(newValue).isValid()
                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                            : ''
                                }
                            })
                        }
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                disabled: readOnly || loading || submitting
                            }
                        }}
                    />
                    <TextField
                        label="Morada"
                        placeholder="Rua / Avenida"
                        fullWidth
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.street}
                        onChange={onFieldChange('street')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Código Postal"
                        placeholder="0000-000"
                        fullWidth
                        value={form.zip}
                        onChange={onFieldChange('zip')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Cidade"
                        placeholder="Cidade"
                        fullWidth
                        value={form.city}
                        onChange={onFieldChange('city')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Distrito / Provincia"
                        placeholder="Distrito ou provincia"
                        fullWidth
                        value={form.province}
                        onChange={onFieldChange('province')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="País"
                        placeholder="PT"
                        fullWidth
                        value={form.country}
                        onChange={onFieldChange('country')}
                        disabled={readOnly || loading || submitting}
                        inputProps={{ maxLength: 2 }}
                    />
                    <TextField
                        label="Telefone"
                        placeholder="912345678"
                        fullWidth
                        value={form.phone}
                        onChange={onFieldChange('phone')}
                        disabled={readOnly || loading || submitting}
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Profissional
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2,
                        ...(readOnly ? readOnlyViewFieldSx : {})
                    }}
                >
                    <TextField
                        label="Cédula"
                        placeholder="Número da cédula"
                        fullWidth
                        value={form.professionalCard}
                        onChange={onFieldChange('professionalCard')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Validade"
                        placeholder="dd/mm/aaaa"
                        fullWidth
                        value={form.professionalCardExpiry}
                        onChange={onFieldChange('professionalCardExpiry')}
                        disabled={readOnly || loading || submitting}
                    />
                    <Box />
                    <TextField
                        label="Local"
                        fullWidth
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.workplace}
                        onChange={onFieldChange('workplace')}
                        disabled={readOnly || loading || submitting}
                    />
                    <Box />
                    <FormControl fullWidth disabled={readOnly || loading || submitting}>
                        <InputLabel id="my-profile-especialidade-label">Especialidade</InputLabel>
                        <Select
                            labelId="my-profile-especialidade-label"
                            label="Especialidade"
                            value={form.clinicianType}
                            onChange={onFieldChange('clinicianType')}
                        >
                            {clinicianTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{
                            m: 0,
                            minHeight: 56,
                            px: 1,
                            border: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            alignItems: 'center'
                        }}
                        control={<Checkbox checked={form.coordinator} disabled />}
                        label="Coordenador"
                    />
                    <TextField
                        label="Observações"
                        placeholder="Notas relevantes"
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.notes}
                        onChange={onFieldChange('notes')}
                        disabled={readOnly || loading || submitting}
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Definições de Videochamada
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2
                    }}
                >
                    <CallPriorityField
                        value={form.callPriority}
                        disabled={readOnly || loading || submitting}
                        onChange={(value) =>
                            setForm((prev) => ({
                                ...prev,
                                callPriority: value
                            }))
                        }
                        sx={{ maxWidth: 380 }}
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Horários
                </Typography>

                <Box sx={{ maxWidth: 420 }}>
                    <AvailabilityScheduleCard
                        activeDays={form.availabilityActiveDays}
                        notes={form.availabilityNotes}
                        editing={!readOnly && !loading && !submitting}
                        onToggleDay={handleToggleAvailabilityDay}
                        onNotesChange={(value) =>
                            setForm((prev) => ({
                                ...prev,
                                availabilityNotes: value
                            }))
                        }
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Alterar Password
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2,
                        maxWidth: 980
                    }}
                >
                    <TextField
                        label="Password atual"
                        fullWidth
                        type={showCurrentPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={passwordForm.currentPassword}
                        onChange={onPasswordFieldChange('currentPassword')}
                        disabled={passwordSubmitting}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon sx={{ color: '#5F6C80' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                                        sx={{ color: '#5F6C80' }}
                                    >
                                        {showCurrentPassword ? (
                                            <VisibilityOffOutlinedIcon />
                                        ) : (
                                            <VisibilityOutlinedIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        label="Nova password"
                        fullWidth
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={passwordForm.newPassword}
                        onChange={onPasswordFieldChange('newPassword')}
                        disabled={passwordSubmitting}
                        helperText={passwordPolicyMessage}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon sx={{ color: '#5F6C80' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        sx={{ color: '#5F6C80' }}
                                    >
                                        {showNewPassword ? (
                                            <VisibilityOffOutlinedIcon />
                                        ) : (
                                            <VisibilityOutlinedIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        label="Confirmar nova password"
                        fullWidth
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        onChange={onPasswordFieldChange('confirmPassword')}
                        disabled={passwordSubmitting}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon sx={{ color: '#5F6C80' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        sx={{ color: '#5F6C80' }}
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOffOutlinedIcon />
                                        ) : (
                                            <VisibilityOutlinedIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                {passwordError ? (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>
                        {passwordError}
                    </Alert>
                ) : null}

                {passwordSuccess ? (
                    <Alert severity="success" sx={{ mt: 2, borderRadius: '14px' }}>
                        {passwordSuccess}
                    </Alert>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={onSubmitPasswordChange}
                        disabled={passwordSubmitting}
                        sx={{
                            minHeight: 48,
                            px: 3,
                            borderRadius: '14px',
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#D63731',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#C7312C',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {passwordSubmitting ? 'A alterar...' : 'Alterar password'}
                    </Button>
                </Box>

                {error ? (
                    <Typography color="error" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                    {readOnly ? (
                        <Fab
                            size="medium"
                            sx={{ bgcolor: '#D63731', color: '#fff', boxShadow: 'none' }}
                            onClick={onEdit}
                            disabled={loading || submitting}
                        >
                            <Box
                                component="img"
                                src="/assets/logos-svg/edit.svg"
                                alt="Editar"
                                sx={{ width: 18, height: 18, display: 'block' }}
                            />
                        </Fab>
                    ) : (
                        <Stack direction="row" spacing={1}>
                            <Fab
                                variant="extended"
                                sx={actionSecondarySx}
                                onClick={onCancel}
                                disabled={submitting}
                            >
                                Cancelar
                            </Fab>
                            <Fab
                                variant="extended"
                                sx={actionPrimarySx}
                                onClick={onSubmit}
                                disabled={loading || submitting}
                            >
                                {submitting ? 'A guardar...' : 'Guardar'}
                            </Fab>
                        </Stack>
                    )}
                </Box>
            </Paper>
            </LocalizationProvider>
        </Box>
    );
};

export default MyProfile;
